export type HomebaseUser = {
  id: string;
  email: string;
  displayName: string;
};

export type HouseholdMember = {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'member';
};

export type WorkoutSession = {
  id: string;
  userId: string;
  workoutDate: string;
  dayIndex: number;
  data: unknown;
};

export type FeedbackKind = 'bug' | 'change';

export type FeedbackSurface = 'web' | 'mobile' | 'cli';

export type FeedbackStatus = 'open' | 'complete';

export type FeedbackItem = {
  id: string;
  userId: string;
  kind: FeedbackKind;
  message: string;
  surface: FeedbackSurface;
  screen: string | null;
  url: string | null;
  userAgent: string | null;
  metadata: unknown;
  status: FeedbackStatus;
  completedAt: string | null;
  completedBy: string | null;
  completionNote: string | null;
  createdAt: string;
};


export type WorkoutSetLog = {
  done: boolean;
  load: string;
  perSideLoad?: string;
  reps: string;
  rir: string;
};

export type WorkoutExerciseLog = {
  sets: WorkoutSetLog[];
  note: string;
};

export type WorkoutSessionData = {
  date: string;
  dayIdx: number;
  completedAt?: string;
  exercises: Record<string, WorkoutExerciseLog>;
};

export type WorkoutExercise = {
  id: string;
  name: string;
  sets: number;
  reps: string;
  rir: number;
  variants: string;
};

export type WorkoutDay = {
  short: string;
  weekday: string;
  name: string;
  focus: string;
  prep: { items: string[] };
  exercises: WorkoutExercise[];
};

export const WORKOUT_DAYS: WorkoutDay[] = [
  {
    short: 'D1', weekday: 'Monday', name: 'Lower Body', focus: 'Strength',
    prep: { items: ['Sled warmup: 5-8 minutes easy forward/backward.', 'Tibialis raise + deep calf raise: 1-2 light sets each.', 'Full range split squat or couch stretch: 1 easy set per side.'] },
    exercises: [
      { id: 'd1-squat', name: 'Squat Variant', sets: 3, reps: '5', rir: 2, variants: 'Back squat, front squat, or safety-bar squat' },
      { id: 'd1-rdl', name: 'Romanian Deadlift', sets: 3, reps: '5', rir: 3, variants: 'Controlled RDL; keep it pain-free' },
      { id: 'd1-single-leg', name: 'Single-Leg Variant', sets: 3, reps: '8', rir: 2, variants: 'Bulgarian split squat, lunge, or pistol squat' },
      { id: 'd1-back-extension', name: 'Back Extension', sets: 2, reps: '15', rir: 3, variants: 'Smooth reps, neutral finish' },
    ],
  },
  {
    short: 'D2', weekday: 'Tuesday', name: 'Upper Body', focus: 'Strength',
    prep: { items: ['Couch stretch: 60-90 seconds per side.', 'Outer hip circuit or pigeon strength: 1 light round.', 'Optional active hang: 30-60 seconds.'] },
    exercises: [
      { id: 'd2-horizontal-push', name: 'Horizontal Push', sets: 3, reps: '5', rir: 2, variants: 'Bench press or dumbbell press' },
      { id: 'd2-horizontal-pull', name: 'Horizontal Pull', sets: 3, reps: '5', rir: 2, variants: 'Cable row, dumbbell row, seal row, machine row, or TRX row' },
      { id: 'd2-vertical-push', name: 'Vertical Push', sets: 2, reps: '8', rir: 2, variants: 'Overhead press, dumbbell press, or landmine press' },
      { id: 'd2-vertical-pull', name: 'Vertical Pull', sets: 2, reps: '8', rir: 2, variants: 'Chin-up, pull-up, assisted pull-up, or lat pulldown' },
      { id: 'd2-flys', name: 'Flys', sets: 2, reps: '15', rir: 2, variants: 'Cable, dumbbell, or machine fly' },
    ],
  },
  {
    short: 'D3', weekday: 'Thursday', name: 'Lower Body', focus: 'Volume',
    prep: { items: ['Sled warmup: 5-8 minutes easy forward/backward.', 'Tibialis raise + deep calf raise: 1-2 light sets each.', 'Full range split squat or couch stretch: 1 easy set per side.'] },
    exercises: [
      { id: 'd3-leg-press', name: 'Leg Press Variant', sets: 3, reps: '8', rir: 2, variants: 'Seated leg press, 45 degree leg press, or hack squat' },
      { id: 'd3-extension', name: 'Leg Extension', sets: 3, reps: '12', rir: 2, variants: 'Machine leg extension' },
      { id: 'd3-curl', name: 'Leg Curl', sets: 3, reps: '12', rir: 2, variants: 'Seated or lying leg curl' },
      { id: 'd3-back-extension', name: 'Back Extension', sets: 2, reps: '15', rir: 3, variants: 'Treat this as the hinge for the day' },
    ],
  },
  {
    short: 'D4', weekday: 'Friday', name: 'Upper Body', focus: 'Volume',
    prep: { items: ['Couch stretch: 60-90 seconds per side.', 'Outer hip circuit or pigeon strength: 1 light round.', 'Optional active hang: 30-60 seconds.'] },
    exercises: [
      { id: 'd4-horizontal-push', name: 'Horizontal Push', sets: 3, reps: '10', rir: 2, variants: 'Bench, dumbbell, machine, decline, or modest incline press' },
      { id: 'd4-horizontal-pull', name: 'Horizontal Pull', sets: 3, reps: '10', rir: 2, variants: 'Cable row, dumbbell row, seal row, machine row, or TRX row' },
      { id: 'd4-incline-push', name: 'Incline Push', sets: 2, reps: '12', rir: 2, variants: 'Dumbbell, barbell, or machine incline press' },
      { id: 'd4-vertical-pull', name: 'Vertical Pull', sets: 2, reps: '12', rir: 2, variants: 'Chin-up, pull-up, assisted pull-up, or lat pulldown' },
      { id: 'd4-triceps', name: 'Triceps Isolation', sets: 2, reps: '12', rir: 2, variants: 'Cable, machine, or free-weight extension' },
      { id: 'd4-biceps', name: 'Biceps Isolation', sets: 2, reps: '12', rir: 2, variants: 'Cable, machine, or dumbbell curl' },
    ],
  },
];

export function localDateFromDate(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function todayWorkoutDate() {
  return localDateFromDate(new Date());
}

export function defaultWorkoutSession(dayIdx: number, date = todayWorkoutDate()): WorkoutSessionData {
  const day = WORKOUT_DAYS[dayIdx] ?? WORKOUT_DAYS[0];
  return {
    date,
    dayIdx,
    exercises: Object.fromEntries(day.exercises.map((exercise) => [exercise.id, {
      note: '',
      sets: Array.from({ length: exercise.sets }, () => ({ done: false, load: '', reps: exercise.reps, rir: String(exercise.rir) })),
    }])),
  };
}

export function normalizeWorkoutSession(data: unknown, dayIdx: number, date: string): WorkoutSessionData {
  const base = defaultWorkoutSession(dayIdx, date);
  const raw = data && typeof data === 'object' ? data as Partial<WorkoutSessionData> : {};
  const exercises = raw.exercises && typeof raw.exercises === 'object' ? raw.exercises : {};
  for (const exercise of WORKOUT_DAYS[dayIdx]?.exercises ?? []) {
    const current = exercises[exercise.id] as Partial<WorkoutExerciseLog> | undefined;
    const baseLog = base.exercises[exercise.id];
    if (!current) continue;
    base.exercises[exercise.id] = {
      note: String(current.note ?? ''),
      sets: baseLog.sets.map((set, index) => ({ ...set, ...(current.sets?.[index] ?? {}) })),
    };
  }
  if (raw.completedAt) base.completedAt = String(raw.completedAt);
  return base;
}

export type WorkoutSessionLike = {
  workoutDate: string;
  dayIndex: number;
  data: unknown;
};

export function workoutProgress(dayIdx: number, data: WorkoutSessionData) {
  const day = WORKOUT_DAYS[dayIdx] ?? WORKOUT_DAYS[0];
  const totalSets = day.exercises.reduce((sum, exercise) => sum + exercise.sets, 0);
  const doneSets = day.exercises.reduce((sum, exercise) => sum + (data.exercises[exercise.id]?.sets ?? []).filter((set) => set.done).length, 0);
  return { totalSets, doneSets, percent: totalSets ? Math.round((doneSets / totalSets) * 100) : 0 };
}

const STANDARD_BARBELL_WEIGHT = 45;

function formatWorkoutLoad(value: number) {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
}

export function barbellPerSideFromTotal(load: string) {
  const total = Number.parseFloat(load);
  if (!Number.isFinite(total) || total <= STANDARD_BARBELL_WEIGHT) return '';
  const perSide = (total - STANDARD_BARBELL_WEIGHT) / 2;
  return perSide > 0 ? formatWorkoutLoad(perSide) : '';
}

export function barbellTotalFromPerSide(perSideLoad: string) {
  const perSide = Number.parseFloat(perSideLoad);
  if (!Number.isFinite(perSide) || perSide < 0) return '';
  return formatWorkoutLoad(STANDARD_BARBELL_WEIGHT + perSide * 2);
}

export function barbellPerSideHint(load: string) {
  const perSide = barbellPerSideFromTotal(load);
  return perSide ? `${perSide} lb per side on 45 lb bar` : '';
}

function hasWorkoutAmount(set: Partial<WorkoutSetLog> | undefined) {
  return Boolean(set?.load || set?.perSideLoad);
}

export function latestWorkoutAmountsByExercise(sessions: WorkoutSessionLike[]) {
  const latest: Record<string, { workoutDate: string; dayIndex: number; log: WorkoutExerciseLog }> = {};

  for (const session of sessions) {
    const data = session.data as Partial<WorkoutSessionData> | undefined;
    if (!data?.exercises || typeof data.exercises !== 'object') continue;

    for (const [exerciseId, log] of Object.entries(data.exercises)) {
      const current = log as Partial<WorkoutExerciseLog> | undefined;
      if (!Array.isArray(current?.sets)) continue;

      const target = latest[exerciseId] ?? {
        workoutDate: session.workoutDate,
        dayIndex: session.dayIndex,
        log: { note: '', sets: [] },
      };

      let captured = Boolean(latest[exerciseId]);
      current.sets.forEach((set, index) => {
        if (hasWorkoutAmount(target.log.sets[index]) || !hasWorkoutAmount(set)) return;
        const load = String(set.load ?? '');
        target.log.sets[index] = {
          done: false,
          load,
          perSideLoad: set.perSideLoad ? String(set.perSideLoad) : barbellPerSideFromTotal(load),
          reps: String(set.reps ?? ''),
          rir: String(set.rir ?? ''),
        };
        captured = true;
      });

      if (captured) latest[exerciseId] = target;
    }
  }

  return latest;
}

export function applyLatestWorkoutAmounts(data: WorkoutSessionData, latest: Record<string, { log: WorkoutExerciseLog } | undefined>): WorkoutSessionData {
  return {
    ...data,
    exercises: Object.fromEntries(Object.entries(data.exercises).map(([exerciseId, log]) => {
      const previous = latest[exerciseId]?.log;
      if (!previous) return [exerciseId, log];

      return [exerciseId, {
        ...log,
        sets: log.sets.map((set, index) => {
          const prior = previous.sets[index];
          if (!prior) return set;

          const next = { ...set };
          if (!next.load && prior.load) next.load = prior.load;
          if (!next.perSideLoad && prior.perSideLoad) next.perSideLoad = prior.perSideLoad;
          if (!next.perSideLoad && next.load) next.perSideLoad = barbellPerSideFromTotal(next.load);
          return next;
        }),
      }];
    })),
  };
}


export type DailySection = 'vitamins' | 'drops';

export type DailyReminderStyle = 'push' | 'push_alarm';

export type DailyItem = {
  id: string;
  section: DailySection;
  name: string;
  instruction: string;
  dose: string;
  order: number;
};

export type DailyConfig = {
  vitaminsEnabled: boolean;
  dropsEnabled: boolean;
  firstNudge: string;
  repeatEveryHours: number;
  stopAt: string;
  timeZone: string;
  style: DailyReminderStyle;
  snoozeHours: number;
  items: DailyItem[];
};

export type DailyDayItem = {
  itemId: string;
  checked: boolean;
  checkedAt: string | null;
  skippedAt: string | null;
};

export type DailyItemStatus = 'pending' | 'done' | 'skipped';

export type DailyDayData = {
  items: DailyDayItem[];
  skippedToday: boolean;
  snoozedUntil: string | null;
};

export const DAILY_SECTIONS: DailySection[] = ['vitamins', 'drops'];

export const DEFAULT_DAILY_ITEMS: DailyItem[] = [
  { id: 'vitamin-d3-k2', section: 'vitamins', name: 'Vitamin D3 + K2', instruction: 'with fat · morning', dose: '2 caps', order: 0 },
  { id: 'magnesium-glycinate', section: 'vitamins', name: 'Magnesium glycinate', instruction: 'before bed', dose: '400 mg', order: 1 },
  { id: 'omega-3', section: 'vitamins', name: 'Omega-3', instruction: 'with food', dose: '2 caps', order: 2 },
  { id: 'sublingual-drops', section: 'drops', name: 'Sublingual drops', instruction: 'under tongue · hold 2 min', dose: '3 sprays', order: 0 },
  { id: 'nasal-rinse', section: 'drops', name: 'Nasal rinse', instruction: 'after drops', dose: '1 rinse', order: 1 },
];

export const FOOD_DIARY_DEFAULT_TIME_ZONE = 'Asia/Tokyo';

export const DEFAULT_DAILY_CONFIG: DailyConfig = {
  vitaminsEnabled: true,
  dropsEnabled: true,
  firstNudge: '09:00',
  repeatEveryHours: 2,
  stopAt: '21:00',
  timeZone: FOOD_DIARY_DEFAULT_TIME_ZONE,
  style: 'push_alarm',
  snoozeHours: 1,
  items: DEFAULT_DAILY_ITEMS,
};

const DAILY_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

function dailyTime(value: unknown, fallback: string) {
  return typeof value === 'string' && DAILY_TIME_PATTERN.test(value) ? value : fallback;
}

function dailyHours(value: unknown, fallback: number) {
  const hours = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  if (!Number.isFinite(hours)) return fallback;
  return Math.min(24, Math.max(0.25, hours));
}

function dailyTimeZone(value: unknown, fallback: string) {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return value;
  } catch {
    return fallback;
  }
}

function dailyDate(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function normalizeDailyItem(input: unknown, index: number): DailyItem | null {
  if (!input || typeof input !== 'object') return null;
  const raw = input as Partial<DailyItem>;
  const id = String(raw.id ?? '').trim();
  const name = String(raw.name ?? '').trim();
  if (!id || !name) return null;
  return {
    id,
    section: raw.section === 'drops' ? 'drops' : 'vitamins',
    name,
    instruction: String(raw.instruction ?? ''),
    dose: String(raw.dose ?? ''),
    order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : index,
  };
}

export function normalizeDailyConfig(input: unknown): DailyConfig {
  const raw = input && typeof input === 'object' ? input as Partial<DailyConfig> : {};
  const items = Array.isArray(raw.items)
    ? raw.items.map(normalizeDailyItem).filter((item): item is DailyItem => Boolean(item))
    : DEFAULT_DAILY_CONFIG.items;
  const seen = new Set<string>();
  return {
    vitaminsEnabled: raw.vitaminsEnabled ?? DEFAULT_DAILY_CONFIG.vitaminsEnabled,
    dropsEnabled: raw.dropsEnabled ?? DEFAULT_DAILY_CONFIG.dropsEnabled,
    firstNudge: dailyTime(raw.firstNudge, DEFAULT_DAILY_CONFIG.firstNudge),
    repeatEveryHours: dailyHours(raw.repeatEveryHours, DEFAULT_DAILY_CONFIG.repeatEveryHours),
    stopAt: dailyTime(raw.stopAt, DEFAULT_DAILY_CONFIG.stopAt),
    timeZone: dailyTimeZone(raw.timeZone, DEFAULT_DAILY_CONFIG.timeZone),
    style: raw.style === 'push' ? 'push' : 'push_alarm',
    snoozeHours: dailyHours(raw.snoozeHours, DEFAULT_DAILY_CONFIG.snoozeHours),
    items: items
      .filter((item) => (seen.has(item.id) ? false : seen.add(item.id)))
      .sort((a, b) => DAILY_SECTIONS.indexOf(a.section) - DAILY_SECTIONS.indexOf(b.section) || a.order - b.order),
  };
}

export function normalizeDailyDay(input: unknown, config: DailyConfig): DailyDayData {
  const raw = input && typeof input === 'object' ? input as Partial<DailyDayData> : {};
  const byId = new Map<string, Partial<DailyDayItem>>();
  for (const item of Array.isArray(raw.items) ? raw.items : []) {
    if (item && typeof item === 'object' && item.itemId) byId.set(String(item.itemId), item);
  }
  return {
    items: config.items.map((item) => {
      const current = byId.get(item.id);
      const checked = Boolean(current?.checked);
      return {
        itemId: item.id,
        checked,
        checkedAt: checked ? dailyDate(current?.checkedAt) : null,
        skippedAt: checked ? null : dailyDate(current?.skippedAt),
      };
    }),
    skippedToday: Boolean(raw.skippedToday),
    snoozedUntil: dailyDate(raw.snoozedUntil),
  };
}

export function dailySectionEnabled(config: DailyConfig, section: DailySection) {
  return section === 'drops' ? config.dropsEnabled : config.vitaminsEnabled;
}

export function dailySectionItems(config: DailyConfig, section: DailySection) {
  return config.items.filter((item) => item.section === section);
}

export function dailyEnabledItems(config: DailyConfig) {
  return config.items.filter((item) => dailySectionEnabled(config, item.section));
}

export function dailyItemStatus(item: DailyDayItem): DailyItemStatus {
  if (item.checked) return 'done';
  if (item.skippedAt) return 'skipped';
  return 'pending';
}

function dailyItemRecord(itemId: string, status: DailyItemStatus, now: Date): DailyDayItem {
  const at = now.toISOString();
  switch (status) {
    case 'done':
      return { itemId, checked: true, checkedAt: at, skippedAt: null };
    case 'skipped':
      return { itemId, checked: false, checkedAt: null, skippedAt: at };
    case 'pending':
      return { itemId, checked: false, checkedAt: null, skippedAt: null };
    default: {
      const unreachable: never = status;
      throw new Error(`Unknown daily status ${unreachable}`);
    }
  }
}

export function dailySectionCounts(config: DailyConfig, day: DailyDayData): Record<DailySection, { done: number; total: number }> {
  const done = new Set(day.items.filter((item) => dailyItemStatus(item) === 'done').map((item) => item.itemId));
  return Object.fromEntries(DAILY_SECTIONS.map((section) => {
    const items = dailySectionItems(config, section);
    return [section, { done: items.filter((item) => done.has(item.id)).length, total: items.length }];
  })) as Record<DailySection, { done: number; total: number }>;
}

export function dailyOutstandingItems(config: DailyConfig, day: DailyDayData) {
  const settled = new Set(day.items.filter((item) => dailyItemStatus(item) !== 'pending').map((item) => item.itemId));
  return dailyEnabledItems(config).filter((item) => !settled.has(item.id));
}

export function dailyOutstandingCount(config: DailyConfig, day: DailyDayData) {
  return dailyOutstandingItems(config, day).length;
}

export function dailyDoneCount(config: DailyConfig, day: DailyDayData) {
  const enabled = new Set(dailyEnabledItems(config).map((item) => item.id));
  return day.items.filter((item) => enabled.has(item.itemId) && dailyItemStatus(item) === 'done').length;
}

export function dailyHasSkippedItem(config: DailyConfig, day: DailyDayData) {
  const enabled = new Set(dailyEnabledItems(config).map((item) => item.id));
  return day.items.some((item) => enabled.has(item.itemId) && dailyItemStatus(item) === 'skipped');
}

export function setDailyItemStatus(day: DailyDayData, itemId: string, status: DailyItemStatus, now = new Date()): DailyDayData {
  return {
    ...day,
    items: day.items.map((item) => (item.itemId === itemId ? dailyItemRecord(itemId, status, now) : item)),
  };
}

export function markAllDailyDone(config: DailyConfig, day: DailyDayData, now = new Date()): DailyDayData {
  const pending = new Set(dailyOutstandingItems(config, day).map((item) => item.id));
  return {
    ...day,
    items: day.items.map((item) => (pending.has(item.itemId) ? dailyItemRecord(item.itemId, 'done', now) : item)),
  };
}

export function skipDailyOutstanding(config: DailyConfig, day: DailyDayData, now = new Date()): DailyDayData {
  const pending = new Set(dailyOutstandingItems(config, day).map((item) => item.id));
  return {
    ...day,
    items: day.items.map((item) => (pending.has(item.itemId) ? dailyItemRecord(item.itemId, 'skipped', now) : item)),
  };
}

export function dailyOutstandingSummary(items: DailyItem[]) {
  const names = items.map((item) => item.name);
  if (!names.length) return 'Nothing left today';
  if (names.length === 1) return `${names[0]} still open`;
  if (names.length === 2) return `${names[0]} and ${names[1]} still open`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more still open`;
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string) {
  let formatter = zoneFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    zoneFormatters.set(timeZone, formatter);
  }
  return formatter;
}

function zoneParts(timeZone: string, at: Date) {
  const parts = zoneFormatter(timeZone).formatToParts(at);
  return (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value ?? 0);
}

function zoneOffsetMs(timeZone: string, at: Date) {
  const part = zoneParts(timeZone, at);
  const wall = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'), part('second'));
  return wall - Math.floor(at.getTime() / 1000) * 1000;
}

export function dailyLocalDate(timeZone: string, now = new Date()) {
  const part = zoneParts(timeZone, now);
  return `${String(part('year')).padStart(4, '0')}-${String(part('month')).padStart(2, '0')}-${String(part('day')).padStart(2, '0')}`;
}

export function dailyZonedTime(timeZone: string, date: string, time: string) {
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const wall = Date.UTC(year, month - 1, day, hours, minutes);
  const guess = wall - zoneOffsetMs(timeZone, new Date(wall));
  return new Date(wall - zoneOffsetMs(timeZone, new Date(guess)));
}

export function dailyNudgeSlots(config: DailyConfig, now = new Date()): Date[] {
  const date = dailyLocalDate(config.timeZone, now);
  const first = dailyZonedTime(config.timeZone, date, config.firstNudge).getTime();
  const stop = dailyZonedTime(config.timeZone, date, config.stopAt).getTime();
  const step = config.repeatEveryHours * 3_600_000;
  if (step <= 0) return first <= stop ? [new Date(first)] : [];
  const slots: Date[] = [];
  for (let at = first; at <= stop; at += step) slots.push(new Date(at));
  return slots;
}

export function nextNudgeTime(config: DailyConfig, day: DailyDayData, now = new Date()): Date | null {
  if (day.skippedToday) return null;
  if (!dailyOutstandingCount(config, day)) return null;

  const stop = dailyZonedTime(config.timeZone, dailyLocalDate(config.timeZone, now), config.stopAt).getTime();
  const snoozed = day.snoozedUntil ? new Date(day.snoozedUntil) : null;
  if (snoozed && snoozed.getTime() > now.getTime()) return snoozed.getTime() <= stop ? snoozed : null;

  return dailyNudgeSlots(config, now).find((slot) => slot.getTime() >= now.getTime()) ?? null;
}

export function dueDailyNudge(config: DailyConfig, day: DailyDayData, now = new Date()): Date | null {
  if (day.skippedToday) return null;
  if (!dailyOutstandingCount(config, day)) return null;

  const snoozed = day.snoozedUntil ? new Date(day.snoozedUntil).getTime() : 0;
  if (snoozed > now.getTime()) return null;

  const due = dailyNudgeSlots(config, now).filter((slot) => slot.getTime() <= now.getTime() && slot.getTime() >= snoozed);
  return due.length ? due[due.length - 1] : null;
}

export type RecipeState = {
  slug: string;
  cookThisWeek: boolean;
  rating: 'liked' | 'disliked' | null;
  notes: string;
  lastCookedAt?: string | null;
  updatedAt?: string;
};

export type GroceryItem = {
  item: string;
  amount: string;
  recipes: string[];
};

export function weekStartDate(date = new Date()) {
  const copy = new Date(date);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return localDateFromDate(copy);
}

export function stateBySlug(states: RecipeState[]) {
  return Object.fromEntries(states.map((state) => [state.slug, state]));
}

export function recipeIngredientAmount(ingredient: { amount?: string; unit?: string; quantity?: number | string }) {
  const qty = ingredient.amount ?? ingredient.quantity;
  if (qty === undefined || qty === null || qty === '') return '';
  let unit = ingredient.unit ?? '';
  if (unit === 'each') unit = '';
  if (unit && Number(qty) > 1 && !unit.endsWith('s')) unit = `${unit}s`;
  return [qty, unit].filter(Boolean).join(' ');
}

export function recipeProteinEmoji(protein?: string | null) {
  const value = (protein ?? '').toLowerCase();
  if (!value) return '';
  if (value.includes('beef') || value.includes('steak')) return '🥩';
  if (value.includes('chicken')) return '🐓';
  if (value.includes('turkey')) return '🦃';
  if (value.includes('pork') || value.includes('bacon')) return '🥓';
  if (value.includes('lamb')) return '🍖';
  if (value.includes('salmon') || value.includes('fish') || value.includes('tuna') || value.includes('cod')) return '🐟';
  if (value.includes('shrimp')) return '🦐';
  if (value.includes('egg')) return '🥚';
  if (value.includes('tofu') || value.includes('bean') || value.includes('lentil')) return '🫘';
  return '';
}

export function isPantryRecipeIngredient(ingredient: { category?: string; pantry?: boolean }) {
  return ingredient.category === 'pantry' || ingredient.pantry === true;
}

export type RecipeOverrideRecord = { slug: string; data: unknown; updatedAt?: string };

export function applyRecipeOverrides<T extends { slug: string }>(recipes: T[], overrides: RecipeOverrideRecord[]): T[] {
  if (!overrides.length) return recipes;
  const map = new Map(overrides.map((override) => [override.slug, override.data]));
  return recipes.map((recipe) => {
    const data = map.get(recipe.slug);
    return data && typeof data === 'object' ? { ...recipe, ...(data as object), slug: recipe.slug } : recipe;
  });
}

const INGREDIENT_UNITS = new Set(['tablespoon', 'teaspoon', 'cup', 'pound', 'lb', 'ounce', 'oz', 'gram', 'g', 'kg', 'ml', 'liter', 'clove', 'can', 'bunch', 'head', 'slice', 'pinch', 'stalk', 'sprig', 'each']);

export type EditableRecipeIngredient = { item?: string; amount?: string; unit?: string; quantity?: number | string; category?: string; pantry?: boolean; optional?: boolean; preparation?: string; groceryItem?: string };

export function ingredientToLine(ingredient: EditableRecipeIngredient) {
  const amount = recipeIngredientAmount(ingredient);
  let line = [amount, ingredient.item].filter(Boolean).join(' ');
  if (ingredient.preparation) line += `, ${ingredient.preparation}`;
  if (ingredient.optional) line += ' (optional)';
  if (isPantryRecipeIngredient(ingredient)) line += ' (pantry)';
  return line;
}

export function parseIngredientLine(line: string): EditableRecipeIngredient | null {
  let text = line.trim();
  if (!text) return null;
  const ingredient: EditableRecipeIngredient = {};
  if (/\(pantry\)\s*$/i.test(text)) { ingredient.pantry = true; text = text.replace(/\s*\(pantry\)\s*$/i, ''); }
  if (/\(optional\)\s*$/i.test(text)) { ingredient.optional = true; text = text.replace(/\s*\(optional\)\s*$/i, ''); }
  const commaIndex = text.indexOf(',');
  if (commaIndex > 0) {
    ingredient.preparation = text.slice(commaIndex + 1).trim();
    text = text.slice(0, commaIndex).trim();
  }
  const quantityMatch = text.match(/^(\d+\s+\d+\/\d+|\d+\/\d+|\d*\.?\d+)\s+(.*)$/);
  if (quantityMatch) {
    ingredient.quantity = quantityMatch[1];
    text = quantityMatch[2];
    const firstWord = text.split(/\s+/)[0] ?? '';
    const singular = firstWord.toLowerCase().replace(/e?s$/, '');
    if (INGREDIENT_UNITS.has(firstWord.toLowerCase()) || INGREDIENT_UNITS.has(singular)) {
      ingredient.unit = INGREDIENT_UNITS.has(firstWord.toLowerCase()) ? firstWord.toLowerCase() : singular;
      text = text.slice(firstWord.length).trim();
    }
  }
  ingredient.item = text.trim();
  if (!ingredient.item) return null;
  ingredient.groceryItem = ingredient.item;
  return ingredient;
}

export function buildGroceryList(recipes: Array<{ slug: string; title: string; ingredients?: Array<{ item?: string; amount?: string; unit?: string; quantity?: string; category?: string; pantry?: boolean }> }>, selectedSlugs: string[]) {
  const selected = new Set(selectedSlugs);
  const items = new Map<string, GroceryItem>();
  for (const recipe of recipes) {
    if (!selected.has(recipe.slug)) continue;
    for (const ingredient of recipe.ingredients ?? []) {
      if (!ingredient.item || isPantryRecipeIngredient(ingredient)) continue;
      const key = ingredient.item.trim().toLowerCase();
      const existing = items.get(key) ?? { item: ingredient.item, amount: '', recipes: [] };
      const amount = recipeIngredientAmount(ingredient);
      if (amount) existing.amount = existing.amount ? `${existing.amount}; ${amount}` : amount;
      if (!existing.recipes.includes(recipe.title)) existing.recipes.push(recipe.title);
      items.set(key, existing);
    }
  }
  return Array.from(items.values()).sort((a, b) => a.item.localeCompare(b.item));
}

export function resolveWorkoutExerciseLog(exercise: WorkoutExercise, log?: Partial<WorkoutExerciseLog> | null): WorkoutExerciseLog {
  const fallbackSets = Array.from({ length: exercise.sets }, () => ({ done: false, load: '', reps: exercise.reps, rir: String(exercise.rir) }));
  return {
    note: String(log?.note ?? ''),
    sets: Array.isArray(log?.sets) ? log.sets.map((set) => ({
      done: Boolean(set?.done),
      load: String(set?.load ?? ''),
      ...(set?.perSideLoad != null ? { perSideLoad: String(set.perSideLoad) } : {}),
      reps: String(set?.reps ?? exercise.reps),
      rir: String(set?.rir ?? exercise.rir),
    })) : fallbackSets,
  };
}

export function latestWorkoutByExercise(sessions: WorkoutSession[]) {
  const latest: Record<string, { workoutDate: string; dayIndex: number; log: WorkoutExerciseLog }> = {};
  for (const session of sessions) {
    const data = session.data as Partial<WorkoutSessionData> | undefined;
    if (!data?.exercises || typeof data.exercises !== 'object') continue;
    for (const [exerciseId, log] of Object.entries(data.exercises)) {
      if (latest[exerciseId]) continue;
      const current = log as Partial<WorkoutExerciseLog> | undefined;
      if (!Array.isArray(current?.sets)) continue;
      latest[exerciseId] = { workoutDate: session.workoutDate, dayIndex: session.dayIndex, log: current as WorkoutExerciseLog };
    }
  }
  return latest;
}

export function parseJsonResponseBody(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('<')) throw new Error('api_returned_html');
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new Error('api_returned_non_json');
  }
}

export type MacroTargets = {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
};

export type MealSource = 'manual' | 'grok-bot';

export type MealItem = {
  id: string;
  mealId: string;
  name: string;
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
  photoUrl: string | null;
  photoKey?: string | null;
  sortOrder: number;
  createdAt: string;
};

export type MealCategory = 'breakfast' | 'lunch' | 'dinner' | 'snacks';

export const MEAL_CATEGORY_ORDER: MealCategory[] = ['breakfast', 'lunch', 'dinner', 'snacks'];

export const MEAL_CATEGORY_LABELS: Record<MealCategory, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snacks: 'Snacks',
};

export type Meal = {
  id: string;
  userId: string;
  eatenAt: string;
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
  photoUrl: string | null;
  photoKey?: string | null;
  notes: string | null;
  restaurant: string | null;
  source: MealSource;
  mealType: MealCategory | null;
  createdAt: string;
  items?: MealItem[];
};

export function computeMealTotalsFromItems(items: MealItem[]): MacroTotals {
  return items.reduce(
    (acc, item) => ({
      calories: acc.calories + (item.calories || 0),
      proteinG: acc.proteinG + (item.proteinG || 0),
      carbsG: acc.carbsG + (item.carbsG || 0),
      fatG: acc.fatG + (item.fatG || 0),
    }),
    { calories: 0, proteinG: 0, carbsG: 0, fatG: 0 }
  );
}

export function getMealDisplayName(meal: Meal): string {
  if (meal.notes) return meal.notes;
  if (meal.items?.length) {
    const names = meal.items.map((item) => item.name).filter(Boolean);
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(' & ');
    if (names.length > 2) return `${names[0]} + ${names.length - 1} more`;
  }
  return 'Meal';
}

export function getMealTotals(meal: Meal): MacroTotals {
  if (meal.items?.length) {
    return computeMealTotalsFromItems(meal.items);
  }
  return {
    calories: meal.calories || 0,
    proteinG: meal.proteinG || 0,
    carbsG: meal.carbsG || 0,
    fatG: meal.fatG || 0,
  };
}

export type MacroTotals = {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
};

export type DayMacroSummary = {
  date: string;
  meals: Meal[];
  totals: MacroTotals;
  targets: MacroTargets | null;
  remaining: MacroTotals | null;
};

export const DEFAULT_MACRO_TARGETS: MacroTargets = {
  calories: 2000,
  proteinG: 150,
  carbsG: 200,
  fatG: 65,
};

export function normalizeMacroTargets(input: unknown): MacroTargets {
  const raw = input && typeof input === 'object' ? input as Partial<MacroTargets> : {};
  return {
    calories: typeof raw.calories === 'number' && raw.calories > 0 ? raw.calories : DEFAULT_MACRO_TARGETS.calories,
    proteinG: typeof raw.proteinG === 'number' && raw.proteinG > 0 ? raw.proteinG : DEFAULT_MACRO_TARGETS.proteinG,
    carbsG: typeof raw.carbsG === 'number' && raw.carbsG > 0 ? raw.carbsG : DEFAULT_MACRO_TARGETS.carbsG,
    fatG: typeof raw.fatG === 'number' && raw.fatG > 0 ? raw.fatG : DEFAULT_MACRO_TARGETS.fatG,
  };
}

export function computeMacroTotals(meals: Meal[]): MacroTotals {
  return meals.reduce((acc, meal) => ({
    calories: acc.calories + (meal.calories ?? 0),
    proteinG: acc.proteinG + (meal.proteinG ?? 0),
    carbsG: acc.carbsG + (meal.carbsG ?? 0),
    fatG: acc.fatG + (meal.fatG ?? 0),
  }), { calories: 0, proteinG: 0, carbsG: 0, fatG: 0 });
}

export function computeRemainingMacros(totals: MacroTotals, targets: MacroTargets): MacroTotals {
  return {
    calories: Math.max(0, targets.calories - totals.calories),
    proteinG: Math.max(0, targets.proteinG - totals.proteinG),
    carbsG: Math.max(0, targets.carbsG - totals.carbsG),
    fatG: Math.max(0, targets.fatG - totals.fatG),
  };
}

export function resolveFoodDiaryTimeZone(timeZone?: string | null): string {
  const resolved = dailyTimeZone(timeZone, FOOD_DIARY_DEFAULT_TIME_ZONE);
  // Food diary buckets are household-local (Asia/Tokyo). Never treat UTC/GMT as the diary zone.
  if (resolved === 'UTC' || resolved === 'Etc/UTC' || resolved === 'GMT' || resolved === 'Etc/GMT') {
    return FOOD_DIARY_DEFAULT_TIME_ZONE;
  }
  return resolved;
}

export function mealLocalDate(eatenAt: string, timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE): string {
  return dailyLocalDate(resolveFoodDiaryTimeZone(timeZone), new Date(eatenAt));
}

export function formatMealTime(eatenAt: string, timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE): string {
  return new Date(eatenAt).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: resolveFoodDiaryTimeZone(timeZone),
  });
}

export function zonedHour(eatenAt: string, timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE): number {
  const part = zoneParts(resolveFoodDiaryTimeZone(timeZone), new Date(eatenAt));
  return part('hour') % 24;
}

export function mealLocalWallClock(
  at: Date | string = new Date(),
  timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE,
): { date: string; time: string } {
  const zone = resolveFoodDiaryTimeZone(timeZone);
  const instant = typeof at === 'string' ? new Date(at) : at;
  const part = zoneParts(zone, instant);
  return {
    date: dailyLocalDate(zone, instant),
    time: `${String(part('hour') % 24).padStart(2, '0')}:${String(part('minute')).padStart(2, '0')}`,
  };
}

/** Interpret a diary wall-clock date+time in the food-diary zone (default Asia/Tokyo), never process-local/UTC. */
export function mealEatenAtFromLocalWallClock(
  date: string,
  time: string,
  timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE,
): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error('meal date must be YYYY-MM-DD');
  }
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    throw new Error('meal time must be HH:MM');
  }
  return dailyZonedTime(resolveFoodDiaryTimeZone(timeZone), date, time).toISOString();
}

export function formatMacroTotalsLine(totals: Pick<MacroTotals, 'calories' | 'proteinG' | 'carbsG' | 'fatG'>): string {
  return `${totals.calories} kcal · ${totals.proteinG}g P · ${totals.carbsG}g C · ${totals.fatG}g F`;
}

export type FoodEntry = {
  key: string;
  kind: 'meal' | 'item';
  mealId: string;
  itemId: string | null;
  name: string;
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  photoUrl: string | null;
  eatenAt: string;
  restaurant: string | null;
  category: MealCategory;
};

export type MealCategorySection = {
  category: MealCategory;
  label: string;
  entries: FoodEntry[];
  totals: MacroTotals;
};

export function isMealCategory(value: unknown): value is MealCategory {
  return value === 'breakfast' || value === 'lunch' || value === 'dinner' || value === 'snacks';
}

export function mealCategoryFromTime(eatenAt: string, timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE): MealCategory {
  const hour = zonedHour(eatenAt, timeZone);
  if (hour >= 5 && hour < 11) return 'breakfast';
  if (hour >= 11 && hour < 16) return 'lunch';
  if (hour >= 16 && hour < 22) return 'dinner';
  return 'snacks';
}

export function resolveMealCategory(
  meal: Pick<Meal, 'mealType' | 'eatenAt'>,
  timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE,
): MealCategory {
  if (isMealCategory(meal.mealType)) return meal.mealType;
  return mealCategoryFromTime(meal.eatenAt, timeZone);
}

export function flattenMealToEntries(meal: Meal, timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE): FoodEntry[] {
  const category = resolveMealCategory(meal, timeZone);
  if (meal.items?.length) {
    return meal.items.map((item) => ({
      key: `item:${item.id}`,
      kind: 'item' as const,
      mealId: meal.id,
      itemId: item.id,
      name: item.name,
      calories: item.calories || 0,
      proteinG: item.proteinG || 0,
      carbsG: item.carbsG || 0,
      fatG: item.fatG || 0,
      photoUrl: item.photoUrl,
      eatenAt: meal.eatenAt,
      restaurant: meal.restaurant,
      category,
    }));
  }

  return [{
    key: `meal:${meal.id}`,
    kind: 'meal' as const,
    mealId: meal.id,
    itemId: null,
    name: getMealDisplayName(meal),
    calories: meal.calories || 0,
    proteinG: meal.proteinG || 0,
    carbsG: meal.carbsG || 0,
    fatG: meal.fatG || 0,
    photoUrl: meal.photoUrl,
    eatenAt: meal.eatenAt,
    restaurant: meal.restaurant,
    category,
  }];
}

function emptyMacroTotals(): MacroTotals {
  return { calories: 0, proteinG: 0, carbsG: 0, fatG: 0 };
}

function addEntryTotals(acc: MacroTotals, entry: FoodEntry): MacroTotals {
  return {
    calories: acc.calories + entry.calories,
    proteinG: acc.proteinG + entry.proteinG,
    carbsG: acc.carbsG + entry.carbsG,
    fatG: acc.fatG + entry.fatG,
  };
}

export function groupMealsByCategory(meals: Meal[], timeZone: string = FOOD_DIARY_DEFAULT_TIME_ZONE): MealCategorySection[] {
  const zone = resolveFoodDiaryTimeZone(timeZone);
  const entries = meals.flatMap((meal) => flattenMealToEntries(meal, zone));
  return MEAL_CATEGORY_ORDER
    .map((category) => {
      const categoryEntries = entries
        .filter((entry) => entry.category === category)
        .sort((a, b) => new Date(a.eatenAt).getTime() - new Date(b.eatenAt).getTime());
      return {
        category,
        label: MEAL_CATEGORY_LABELS[category],
        entries: categoryEntries,
        totals: categoryEntries.reduce(addEntryTotals, emptyMacroTotals()),
      };
    })
    .filter((section) => section.entries.length > 0);
}

export function computeFoodEntryTotals(entries: FoodEntry[]): MacroTotals {
  return entries.reduce(addEntryTotals, emptyMacroTotals());
}

export type FoodEntryDeleteTarget =
  | { kind: 'item'; mealId: string; itemId: string }
  | { kind: 'meal'; mealId: string };

export function foodEntryDeleteTarget(entry: Pick<FoodEntry, 'mealId' | 'itemId'>): FoodEntryDeleteTarget {
  if (entry.itemId) {
    return { kind: 'item', mealId: entry.mealId, itemId: entry.itemId };
  }
  return { kind: 'meal', mealId: entry.mealId };
}

export function removeFoodEntryFromMeals(meals: Meal[], target: FoodEntryDeleteTarget): Meal[] {
  if (target.kind === 'meal') {
    return meals.filter((meal) => meal.id !== target.mealId);
  }

  return meals.map((meal) => {
    if (meal.id !== target.mealId) return meal;
    const items = (meal.items ?? []).filter((item) => item.id !== target.itemId);
    return { ...meal, items };
  });
}

export type MealSitting = {
  id: string;
  meals: Meal[];
  startTime: string;
  endTime: string;
  totals: MacroTotals;
};

const SITTING_WINDOW_MS = 45 * 60 * 1000;

export function clusterMealsBySitting(meals: Meal[]): MealSitting[] {
  if (!meals.length) return [];

  const sorted = [...meals].sort((a, b) => new Date(a.eatenAt).getTime() - new Date(b.eatenAt).getTime());
  const sittings: MealSitting[] = [];
  let current: Meal[] = [sorted[0]];
  let lastTime = new Date(sorted[0].eatenAt).getTime();

  for (let i = 1; i < sorted.length; i++) {
    const meal = sorted[i];
    const mealTime = new Date(meal.eatenAt).getTime();

    if (mealTime - lastTime <= SITTING_WINDOW_MS) {
      current.push(meal);
    } else {
      sittings.push(buildSitting(current));
      current = [meal];
    }
    lastTime = mealTime;
  }

  if (current.length) {
    sittings.push(buildSitting(current));
  }

  return sittings;
}

function buildSitting(meals: Meal[]): MealSitting {
  const sorted = [...meals].sort((a, b) => new Date(a.eatenAt).getTime() - new Date(b.eatenAt).getTime());
  const totals = sorted.reduce(
    (acc, meal) => {
      const mealTotals = getMealTotals(meal);
      return {
        calories: acc.calories + mealTotals.calories,
        proteinG: acc.proteinG + mealTotals.proteinG,
        carbsG: acc.carbsG + mealTotals.carbsG,
        fatG: acc.fatG + mealTotals.fatG,
      };
    },
    { calories: 0, proteinG: 0, carbsG: 0, fatG: 0 },
  );

  return {
    id: sorted[0].id,
    meals: sorted,
    startTime: sorted[0].eatenAt,
    endTime: sorted[sorted.length - 1].eatenAt,
    totals,
  };
}

export function getSittingDisplayName(sitting: MealSitting): string {
  if (sitting.meals.length === 1) {
    return getMealDisplayName(sitting.meals[0]);
  }

  const allItems: string[] = [];
  for (const meal of sitting.meals) {
    if (meal.items?.length) {
      allItems.push(...meal.items.map((item) => item.name).filter(Boolean));
    } else if (meal.notes) {
      allItems.push(meal.notes);
    }
  }

  if (allItems.length === 0) return 'Meal';
  if (allItems.length === 1) return allItems[0];
  if (allItems.length === 2) return allItems.join(' & ');
  return `${allItems[0]} + ${allItems.length - 1} more`;
}

export function getSittingThumbnail(sitting: MealSitting): string | null {
  for (const meal of sitting.meals) {
    if (meal.photoUrl) return meal.photoUrl;
    const itemThumb = meal.items?.find((item) => item.photoUrl)?.photoUrl;
    if (itemThumb) return itemThumb;
  }
  return null;
}

export function getSittingItemCount(sitting: MealSitting): number {
  let count = 0;
  for (const meal of sitting.meals) {
    if (meal.items?.length) {
      count += meal.items.length;
    } else {
      count += 1;
    }
  }
  return count;
}

/** Personal taste rating — never Google review stars. */
export type RestaurantTasteRating = 'like' | 'meh' | 'dislike';

export type RestaurantVisitWho = 'ben' | 'linda' | 'both';

export type Restaurant = {
  id: string;
  householdId: string;
  name: string;
  area: string;
  /** Tabelog listing URL only — never Google Maps/reviews as source of truth. */
  tabelogUrl: string | null;
  /** Tabelog score string (e.g. "3.51"), never Google rating. */
  tabelogScore: string | null;
  createdAt: string;
  updatedAt: string;
  latestVisit?: RestaurantVisit | null;
  visitCount?: number;
};

export type RestaurantVisit = {
  id: string;
  householdId: string;
  restaurantId: string;
  visitedOn: string;
  who: RestaurantVisitWho;
  dishes: string;
  rating: RestaurantTasteRating;
  note: string | null;
  mealId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  restaurant?: Pick<Restaurant, 'id' | 'name' | 'area' | 'tabelogUrl' | 'tabelogScore'> | null;
};

export const RESTAURANT_TASTE_RATINGS: RestaurantTasteRating[] = ['like', 'meh', 'dislike'];
export const RESTAURANT_VISIT_WHO: RestaurantVisitWho[] = ['ben', 'linda', 'both'];

export function isRestaurantTasteRating(value: unknown): value is RestaurantTasteRating {
  return value === 'like' || value === 'meh' || value === 'dislike';
}

export function isRestaurantVisitWho(value: unknown): value is RestaurantVisitWho {
  return value === 'ben' || value === 'linda' || value === 'both';
}

export function restaurantTasteLabel(rating: RestaurantTasteRating): string {
  if (rating === 'like') return 'Liked';
  if (rating === 'meh') return 'Meh';
  return 'Disliked';
}

// ---------- mini apps ----------

export type HomebaseAppId = 'daily' | 'pregnancy' | 'food' | 'recipes' | 'workouts' | 'settings';

export type HomebaseApp = { id: HomebaseAppId; label: string; tagline: string; accent: string; accentSoft: string; accentDeep: string };

/** Every mini app in the Homebase super app. Order is the launcher order. */
export const HOMEBASE_APPS: HomebaseApp[] = [
  { id: 'daily', label: 'Daily', tagline: 'Vitamins and allergy drops', accent: '#e89a00', accentSoft: '#fdf0d2', accentDeep: '#8a5a00' },
  { id: 'pregnancy', label: 'Pregnancy', tagline: 'Weeks, plan, and visits', accent: '#d6457e', accentSoft: '#fbe3ec', accentDeep: '#9c2456' },
  { id: 'workouts', label: 'Lifts', tagline: 'Four-day lifting log', accent: '#2d5be3', accentSoft: '#e0e8fd', accentDeep: '#1f43ad' },
  { id: 'food', label: 'Food', tagline: 'Meals, macros, restaurants', accent: '#e2462e', accentSoft: '#fce4de', accentDeep: '#a8301c' },
  { id: 'recipes', label: 'Recipes', tagline: 'Library, week plan, groceries', accent: '#2b8a57', accentSoft: '#dcf0e3', accentDeep: '#1d6a41' },
  { id: 'settings', label: 'Settings', tagline: 'Household and accounts', accent: '#6e56cf', accentSoft: '#ece8fa', accentDeep: '#4b3796' },
];

export const MAX_PINNED_APPS = 4;
export const DEFAULT_PINNED_APPS: HomebaseAppId[] = ['daily', 'pregnancy'];

export function isHomebaseAppId(value: unknown): value is HomebaseAppId {
  return HOMEBASE_APPS.some((app) => app.id === value);
}

/** Pinned apps live in user preferences under `pinnedApps`. */
export function normalizePinnedApps(value: unknown): HomebaseAppId[] {
  if (!Array.isArray(value)) return [...DEFAULT_PINNED_APPS];
  return [...new Set(value.filter(isHomebaseAppId))].slice(0, MAX_PINNED_APPS);
}

export function togglePinnedApp(pinned: HomebaseAppId[], id: HomebaseAppId): HomebaseAppId[] {
  if (pinned.includes(id)) return pinned.filter((entry) => entry !== id);
  return pinned.length >= MAX_PINNED_APPS ? pinned : [...pinned, id];
}
